import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DataTable, type Column } from '../../components/table/DataTable';
import { LookupFieldSelect } from '../../components/formFields/LookupFieldSelect';
import {
  getNationalities,
  NATIONALITY_DESCRIPTION_MAX_LENGTH,
  NATIONALITY_NAME_MAX_LENGTH,
  type NationalityListRow,
} from '../../services/nationalityMasters';
import { MD_PERMISSIONS as MD } from '../../rbac/metadataPermissions';
import { PermissionGate } from '../../rbac/guards';
import { consumeFlashToast } from '../../utils/flashToast';
import { useAuth } from '../../contexts/AuthContext';

const EMPTY = '\u2014';
type StatusFilter = '' | 'active' | 'inactive';

function filterNationalities(
  rows: NationalityListRow[],
  filters: { name: string; description: string; status: StatusFilter },
): NationalityListRow[] {
  const nameQ = filters.name.trim().toLowerCase();
  const descQ = filters.description.trim().toLowerCase();
  return rows.filter((row) => {
    if (nameQ && !row.name.toLowerCase().includes(nameQ)) return false;
    if (descQ && !row.description.toLowerCase().includes(descQ)) return false;
    if (filters.status === 'active' && !row.active) return false;
    if (filters.status === 'inactive' && row.active) return false;
    return true;
  });
}

export default function NationalitiesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [allRows, setAllRows] = useState<NationalityListRow[]>([]);
  const [reloadToken, setReloadToken] = useState(0);

  const [draftName, setDraftName] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [draftStatus, setDraftStatus] = useState<StatusFilter>('');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<StatusFilter>('');

  const [uiPage, setUiPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    const flash = consumeFlashToast();
    if (flash?.message) {
      setToast(flash.message);
      window.setTimeout(() => setToast(null), 3200);
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getNationalities();
      if (!res.success) throw new Error(res.message);
      setAllRows(res.data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('metadata.nationalities.loadFailed'));
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  }, [reloadToken, t]);

  useEffect(() => {
    if (!user) return;
    void loadList();
  }, [user?.id, loadList]);

  const filteredRows = useMemo(
    () => filterNationalities(allRows, { name, description, status }),
    [allRows, description, name, status],
  );

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const safePage = Math.min(uiPage, totalPages);
  const pageRows = filteredRows.slice((safePage - 1) * pageSize, safePage * pageSize);

  const columns = useMemo<Column<NationalityListRow>[]>(() => {
    return [
      {
        key: 'name',
        header: t('metadata.nationalities.nationalityName'),
        getValue: (r) => r.name,
        render: (r) => <span style={{ fontWeight: 600 }}>{r.name || EMPTY}</span>,
      },
      {
        key: 'code',
        header: t('metadata.nationalities.nationalityCode'),
        getValue: (r) => r.code,
        render: (r) => <span style={{ color: 'var(--t2)' }}>{r.code || EMPTY}</span>,
      },
      {
        key: 'description',
        header: t('metadata.nationalities.description'),
        getValue: (r) => r.description,
        render: (r) => r.description || EMPTY,
      },
      {
        key: 'active',
        header: t('common.status'),
        getValue: (r) => (r.active ? t('common.active') : t('common.inactive')),
        render: (r) => (
          <span className={`al-pill al-pill-${r.active ? 'file-d' : 'user-d'}`}>
            {r.active ? t('common.active') : t('common.inactive')}
          </span>
        ),
      },
      {
        key: 'actions',
        header: t('common.actions'),
        getValue: () => '',
        searchable: false,
        sortable: false,
        render: (r) => (
          <div className="act-wrap">
            <PermissionGate requirement={{ anyPermissions: [MD.MANAGE_MASTER_DATA] }}>
              <Link
                className="act-b"
                to={`/metadata/nationalities/${encodeURIComponent(r.id)}/edit`}
                data-tip={t('metadata.nationalities.updateNationality')}
                aria-label={t('metadata.nationalities.updateNationality')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </Link>
            </PermissionGate>
          </div>
        ),
      },
    ];
  }, [t]);

  const resetFilters = () => {
    setDraftName('');
    setDraftDescription('');
    setDraftStatus('');
    setName('');
    setDescription('');
    setStatus('');
    setUiPage(1);
    setPageSize(10);
    setReloadToken((n) => n + 1);
  };

  const applyFilters = () => {
    setName(draftName.trim());
    setDescription(draftDescription.trim());
    setStatus(draftStatus);
    setUiPage(1);
    setReloadToken((n) => n + 1);
  };

  return (
    <>
      <div className="bc">
        <span>{t('nav.groups.metadata')}</span>
        <span className="bc-sep">/</span>
        {t('metadata.nationalities.title')}
      </div>

      <div className="page-hdr">
        <div>
          <div className="pg-title">{t('metadata.nationalities.title')}</div>
        </div>
        <PermissionGate requirement={{ anyPermissions: [MD.MANAGE_MASTER_DATA] }}>
          <Link to="/metadata/nationalities/new" className="btn-create" style={{ textDecoration: 'none' }}>
            {t('metadata.nationalities.addNationality')}
          </Link>
        </PermissionGate>
      </div>

      {toast ? <div className="si-ok" style={{ marginBottom: 12 }}>{toast}</div> : null}

      <div className="flt-card">
        <div className="flt-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
          <div className="flt-f">
            <div className="flt-l">{t('metadata.nationalities.nationalityName')}</div>
            <input
              className="al-inp"
              value={draftName}
              maxLength={NATIONALITY_NAME_MAX_LENGTH}
              placeholder={t('metadata.nationalities.nationalityNamePlaceholder')}
              onChange={(e) => setDraftName(e.target.value)}
            />
          </div>
          <div className="flt-f">
            <div className="flt-l">{t('metadata.nationalities.description')}</div>
            <input
              className="al-inp"
              value={draftDescription}
              maxLength={NATIONALITY_DESCRIPTION_MAX_LENGTH}
              placeholder={t('metadata.nationalities.descriptionPlaceholder')}
              onChange={(e) => setDraftDescription(e.target.value)}
            />
          </div>
          <LookupFieldSelect
            label={t('common.status')}
            value={draftStatus}
            placeholder={t('metadata.nationalities.selectStatus')}
            options={[
              { id: 'active', label: t('common.active') },
              { id: 'inactive', label: t('common.inactive') },
            ]}
            onChange={(v) => setDraftStatus(v as StatusFilter)}
          />
        </div>
        <div className="flt-actions">
          <button className="flt-btn" type="button" onClick={resetFilters}>
            {t('common.reset')}
          </button>
          <button className="flt-btn pr" type="button" onClick={applyFilters}>
            {t('common.search')}
          </button>
        </div>
      </div>

      {error ? <div className="si-err">{error}</div> : null}

      <DataTable
        title={t('metadata.nationalities.tableTitle')}
        loading={loading}
        enableHeaderSearch={false}
        emphasizedHeader
        columns={columns}
        rows={pageRows}
        rowKey={(r) => r.id}
        defaultRowsPerPage={10}
        rowsPerPageOptions={[5, 10, 15, 25]}
        footerCountNoun={t('metadata.nationalities.footerCountNoun')}
        previousLabel={t('common.previous')}
        nextLabel={t('common.next')}
        serverPaging={{
          totalCount: filteredRows.length,
          totalPages,
          page: safePage,
          pageSize,
          onPageChange: (p) => setUiPage(p),
          onPageSizeChange: (n) => {
            setPageSize(n);
            setUiPage(1);
          },
        }}
      />
    </>
  );
}
